import { useCallback, useState } from 'react';

import { apiService } from '../../services';
import type { LoadContractDetailsParams } from '../../types/hookType';
import type {
  ApiCoverageItem,
  ApiUniformCapitalItem,
  ContractCoveragesResponse,
  ContractHeaderResponse,
  ContractHolderAddressResponse,
  ContractHolderDataResponse,
  UseVidaContractReturn,
} from '../../types';

const defaultCoverages = (subProductName: string): ContractCoveragesResponse => ({
  subProductName,
  minimumAge: 0,
  maxAgeFirstInvoice: 0,
  maxAgeOtherInvoices: 0,
  capitalBaseValue: 0,
  deathPercentageBasic: 0,
  deathCapitalLimit: 0,
  deathFixedCapital: 0,
  deathQuantity: 0,
  deathPremium: 0,
  deathWaitingPeriod: 0,
  deathWaitingType: '',
  deathDeductible: 0,
  deathDeductibleType: '',
  permanentDisabilityPercentageBasic: 0,
  permanentDisabilityCapitalLimit: 0,
  permanentDisabilityFixedCapital: 0,
  permanentDisabilityQuantity: 0,
  permanentDisabilityPremium: 0,
  permanentDisabilityWaitingPeriod: 0,
  permanentDisabilityWaitingType: '',
  permanentDisabilityDeductible: 0,
  permanentDisabilityDeductibleType: '',
  funeralAidPercentageBasic: 0,
  funeralAidCapitalLimit: 0,
  funeralAidFixedCapital: 0,
  funeralAidQuantity: 0,
  funeralAidPremium: 0,
  funeralAidWaitingPeriod: 0,
  funeralAidWaitingType: '',
  funeralAidDeductible: 0,
  funeralAidDeductibleType: '',
});

function parseApiDate(dateStr: string): string {
  if (!dateStr) return '';
  const part = dateStr.split(' ')[0] || dateStr;
  return part.trim();
}

function mapEstipulanteToHolderData(item: {
  numDocumentoOut: string;
  nomeEstipulanteOut: string;
  nomeFantasiaEstipOut: string;
  dataIniVigenciaOut: string;
  dataFimVigenciaOut: string;
}): ContractHolderDataResponse {
  return {
    documentNumber: item.numDocumentoOut ?? '',
    corporateName: item.nomeEstipulanteOut ?? '',
    tradeName: item.nomeFantasiaEstipOut ?? '',
    validityStart: parseApiDate(item.dataIniVigenciaOut),
    validityEnd: parseApiDate(item.dataFimVigenciaOut),
  };
}

function mapEstipulanteToHolderAddress(item: {
  enderecoEstOut: string;
  complementoEstOut: string;
  bairroEstOut: string;
  cidadeEstOut: string;
  cepEstOut: string;
  ufEstOut: string;
}): ContractHolderAddressResponse {
  const street = [item.enderecoEstOut, item.complementoEstOut].filter(Boolean).join(', ');
  return {
    zipCode: item.cepEstOut ?? '',
    street: street || '-',
    district: item.bairroEstOut ?? '',
    city: item.cidadeEstOut ?? '',
    state: item.ufEstOut ?? '',
  };
}

function mapEstipulanteToHeader(
  item: {
    identContratoOut: number;
    codprodutoOut: number;
    nomeProdutoOut: string;
    nomeEstipulanteOut: string;
  }
): ContractHeaderResponse {
  return {
    contractNumber: String(item.identContratoOut),
    subProductCode: String(item.codprodutoOut),
    subProductName: item.nomeProdutoOut ?? '',
    companyName: item.nomeEstipulanteOut ?? '',
  };
}

function mapCoverageItemsToContractCoverages(
  content: ApiCoverageItem[],
  subProductName: string,
  minimumAge: number,
  maxAgeFirstInvoice: number,
  maxAgeOtherInvoices: number,
  capitalBaseValue: number
): ContractCoveragesResponse {
  const cov = defaultCoverages(subProductName);
  cov.minimumAge = minimumAge;
  cov.maxAgeFirstInvoice = maxAgeFirstInvoice;
  cov.maxAgeOtherInvoices = maxAgeOtherInvoices;
  cov.capitalBaseValue = capitalBaseValue;

  const parseNum = (v: string | number): number => (typeof v === 'string' ? parseFloat(v) || 0 : v);
  const parseIntSafe = (v: string): number => parseInt(v, 10) || 0;

  content.forEach((c) => {
    const name = (c.nomeCoberturaOut || '').toUpperCase();
    const percent = parseNum(c.percentSobreCobBasicaOut);
    const capitalLimit = parseNum(c.valorLimiteCapital);
    const fixedCapital = c.valorCapitalFixo ?? 0;
    const premium = parseNum(c.valorPremioOut) || (c.valorPremio ?? 0);
    const carencia = parseIntSafe(String(c.carencia));
    const franquia = parseNum(c.franquia);

    if (name.includes('MORTE')) {
      cov.deathPercentageBasic = percent / 100;
      cov.deathCapitalLimit = capitalLimit;
      cov.deathFixedCapital = fixedCapital;
      cov.deathQuantity = c.qtdEventos ?? 1;
      cov.deathPremium = premium;
      cov.deathWaitingPeriod = carencia;
      cov.deathWaitingType = c.tipoCarencia || 'DIAS';
      cov.deathDeductible = franquia;
      cov.deathDeductibleType = c.tipoFranquia || 'NAO_APLICAVEL';
    } else if (name.includes('INVALIDEZ') || name.includes('PERMANENTE')) {
      cov.permanentDisabilityPercentageBasic = percent / 100;
      cov.permanentDisabilityCapitalLimit = capitalLimit;
      cov.permanentDisabilityFixedCapital = fixedCapital;
      cov.permanentDisabilityQuantity = c.qtdEventos ?? 1;
      cov.permanentDisabilityPremium = premium;
      cov.permanentDisabilityWaitingPeriod = carencia;
      cov.permanentDisabilityWaitingType = c.tipoCarencia || 'DIAS';
      cov.permanentDisabilityDeductible = franquia;
      cov.permanentDisabilityDeductibleType = c.tipoFranquia || 'NAO_APLICAVEL';
    } else if (name.includes('FUNERAL') || name.includes('AUXÍLIO')) {
      cov.funeralAidPercentageBasic = percent / 100;
      cov.funeralAidCapitalLimit = capitalLimit;
      cov.funeralAidFixedCapital = fixedCapital;
      cov.funeralAidQuantity = c.qtdEventos ?? 1;
      cov.funeralAidPremium = premium;
      cov.funeralAidWaitingPeriod = carencia;
      cov.funeralAidWaitingType = c.tipoCarencia || 'DIAS';
      cov.funeralAidDeductible = franquia;
      cov.funeralAidDeductibleType = c.tipoFranquia || 'NAO_APLICAVEL';
    }
  });

  return cov;
}

function capitalBaseFromUniforme(content: ApiUniformCapitalItem[]): number {
  const first = content[0];
  if (!first || !first.valorCapitalUniforme) return 0;
  const n = parseFloat(String(first.valorCapitalUniforme));
  return Number.isFinite(n) ? n : 0;
}

export const useVidaContract = (): UseVidaContractReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contractData, setContractData] = useState<{
    holderData?: ContractHolderDataResponse;
    holderAddress?: ContractHolderAddressResponse;
    coverages?: ContractCoveragesResponse;
    header?: ContractHeaderResponse;
  } | null>(null);

  const loadContractDetails = useCallback(async (params: LoadContractDetailsParams): Promise<void> => {
    setIsLoading(true);
    setError(null);

    const {
      identContrato,
      identSubestipulante,
      identContratoEst,
      identAbertoPorCoberetura,
      numApoliceEst,
      codEmpresaEst,
      codSucursalEst,
      codRamoEst,
      codprodutoEst,
      certificadoAp,
      identEndosso,
      page = 0,
      size,
    } = params;

    try {
      const [
        coverageRes,
        estipulanteRes,
        capitalUniformeRes,
        ageMinRes,
        ageFirstRes,
        ageOtherRes,
      ] = await Promise.all([
        apiService.contracts.getCoverage(
          identContrato,
          identSubestipulante,
          certificadoAp,
          identEndosso,
          page,
          size
        ),
        apiService.contracts.getEstipulanteData(
          identContratoEst,
          codEmpresaEst,
          codSucursalEst,
          codRamoEst,
          codprodutoEst,
          numApoliceEst
        ),
        apiService.contracts.getCapitalUniforme(
          identSubestipulante,
          identAbertoPorCoberetura,
          page,
          size
        ),
        apiService.contracts.getAgeLimitMin(identSubestipulante),
        apiService.contracts.getAgeLimitFirstInvoice(identSubestipulante),
        apiService.contracts.getAgeLimitOtherInvoices(identSubestipulante),
      ]);

      const estipulante = estipulanteRes.retorno?.[0];
      const subProductName = estipulante?.nomeProdutoOut ?? '';
      const minimumAge = ageMinRes.retorno?.[0]?.valorLimiteIdadeMinima ?? 0;
      const maxAgeFirstInvoice = ageFirstRes.retorno?.[0]?.valorLimiteIdadeMaximaPrimeiraFatura ?? 0;
      const maxAgeOtherInvoices = ageOtherRes.retorno?.[0]?.valorIdadeMaximaDemaisFaturas ?? 0;
      const capitalBaseValue = capitalBaseFromUniforme(capitalUniformeRes.pagina?.content ?? []);
      const coverageContent = coverageRes.pagina?.content ?? [];

      const coverages = mapCoverageItemsToContractCoverages(
        coverageContent,
        subProductName,
        minimumAge,
        maxAgeFirstInvoice,
        maxAgeOtherInvoices,
        capitalBaseValue
      );

      setContractData({
        holderData: estipulante ? mapEstipulanteToHolderData(estipulante) : undefined,
        holderAddress: estipulante ? mapEstipulanteToHolderAddress(estipulante) : undefined,
        header: estipulante ? mapEstipulanteToHeader(estipulante) : undefined,
        coverages,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar detalhes do contrato';
      setError(errorMessage);
      setContractData(null);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    contracts: [],
    isLoading,
    error,
    clearError,
    contractData,
    loadContractDetails,
  };
};
